import React from 'react';
import { Box, Typography, Paper, Button } from '@mui/material';
import SimCardIcon from '@mui/icons-material/SimCard';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import { SectionVariable } from '../types';
import { useSettings } from '../Settings';
import { getAssetUrl, resolveAssetUrl } from '../api/assets';
import { openSalesOrWhatsApp } from '../utils/links';
import { colors, labelTagDarkSx, labelTagSx } from '../theme/tokens';
import { useTranslation, interpolate } from '../i18n';

const IconContainer = ({ children }: { children: React.ReactNode }) => (
  <Box
    sx={{
      width: '6.25rem',
      height: '6.25rem',
      borderRadius: '1.25rem',
      bgcolor: 'var(--color-primary, #6B00D6)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      mb: 4,
    }}
  >
    <Box
      sx={{
        width: '60%',
        height: '60%',
        borderRadius: '0.8rem',
        bgcolor: colors.overlayWhite15,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <Box sx={{ color: '#FFF', display: 'flex' }}>
        {children}
      </Box>
    </Box>
  </Box>
);


const ComprarButton = ({ onClick, label }: { onClick?: () => void; label: string }) => (
  <Button
    onClick={onClick}
    variant="contained"
    sx={{
      mt: 'auto',
      bgcolor: 'var(--color-primary, #6B00D6)',
      color: '#fff',
      borderRadius: '24px',
      textTransform: 'none',
      fontFamily: "'Figtree', sans-serif",
      fontWeight: 600,
      fontSize: '0.9rem',
      pl: 2.5,
      pr: 1,
      py: 0.5,
      width: 'fit-content',
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      boxShadow: 'none',
      '&:hover': {
        bgcolor: 'var(--color-primary, #6B00D6)',
        boxShadow: 'none',
      },
    }}
  >
    {label}
    <Box sx={{ width: 28, height: 28, bgcolor: '#fff', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="var(--color-primary, #6B00D6)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M5 12h14M12 5l7 7-7 7"/>
        </svg>
    </Box>
  </Button>
);

interface FeaturesProps {
  variables?: SectionVariable;
  name?: string;
}

export function Features({ variables }: FeaturesProps) {
  const { app, settings } = useSettings();
  const { t } = useTranslation();
  const brand = settings.companyName || 'White';

  function handleBuy() {
    const message = interpolate(t.whatsapp.salesMessage, {
      brand: settings.companyName || t.whatsapp.salesFallbackBrand,
    });
    openSalesOrWhatsApp(settings.salesUrl, settings.socials?.whatsapp, message);
  }
  const aboutImage = variables?.image?.[0]
    ? resolveAssetUrl(variables.image[0], app)
    : getAssetUrl('images/woman-phone.png', app);

  return (
    <Box
      className="features"
      sx={{
        py: { xs: 6, md: 8 },
        px: { xs: 2, md: 4 },
        maxWidth: 1280,
        mx: 'auto',
      }}
    >
      <Box
        sx={{
          ...labelTagSx,
          display: 'inline-flex',
          mb: 2,
        }}
      >
        {t.features.tag}
      </Box>
      <Typography
        variant="h4"
        sx={{
          color: '#323232',
          fontFamily: "'Figtree', sans-serif",
          fontSize: '2.1875rem',
          fontStyle: 'normal',
          fontWeight: 500,
          lineHeight: '2.5rem',
          mb: 6,
        }}
      >
        {t.features.titleLine1}
        <br />
        {t.features.titleLine2}
      </Typography>

      <Box
        sx={{
          display: 'flex',
          gap: 3,
          overflowX: { xs: 'auto', md: 'visible' },
          flexWrap: { xs: 'nowrap', md: 'wrap' },
          scrollSnapType: { xs: 'x mandatory', md: 'none' },
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
          pb: { xs: 2, md: 0 },
        }}
      >
        {/* Chip Card */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            borderRadius: '24px',
            border: '1px solid #f0f0f0',
            minWidth: { xs: '280px', md: 0 },
            flex: { xs: '0 0 auto', md: '1 1 22%' },
            scrollSnapAlign: 'start',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <IconContainer>
            <SimCardIcon sx={{ fontSize: '2rem' }} />
          </IconContainer>

          <Box display="flex" gap={1} mb={3}>
            <Box sx={{ border: '1px solid #EAEAEA', borderRadius: '4px', px: 1, py: 0.5, fontSize: '0.6rem', fontFamily: "'Figtree', sans-serif", fontWeight: 600, color: '#323232' }}>{t.features.physicalChip}</Box>
            <Box sx={{ border: '1px solid #EAEAEA', borderRadius: '4px', px: 1, py: 0.5, fontSize: '0.6rem', fontFamily: "'Figtree', sans-serif", fontWeight: 600, color: '#323232' }}>{t.features.eSim}</Box>
          </Box>

          <Typography sx={{ color: '#323232', fontFamily: "'Figtree', sans-serif", fontSize: '1.5rem', fontWeight: 500, lineHeight: '1.8125rem', mb: 1 }}>
            {t.features.chipTitle}
          </Typography>
          <Typography sx={{ color: '#A0A0A0', fontFamily: "'Figtree', sans-serif", fontSize: '0.875rem', fontWeight: 400, mb: 4 }}>
            {t.features.chipDesc}
          </Typography>

          <ComprarButton onClick={handleBuy} label={t.common.buy} />
        </Paper>

        {/* Portability Card */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            borderRadius: '24px',
            border: '1px solid #f0f0f0',
            minWidth: { xs: '280px', md: 0 },
            flex: { xs: '0 0 auto', md: '1 1 22%' },
            scrollSnapAlign: 'start',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <IconContainer>
            <SwapHorizIcon sx={{ fontSize: '2rem' }} />
          </IconContainer>

          <Box display="flex" mb={3}>
            <Box sx={{ border: '1px solid #EAEAEA', borderRadius: '4px', px: 1, py: 0.5, fontSize: '0.6rem', fontFamily: "'Figtree', sans-serif", fontWeight: 600, color: '#323232' }}>{t.features.portabilityTag}</Box>
          </Box>

          <Typography sx={{ color: '#323232', fontFamily: "'Figtree', sans-serif", fontSize: '1.5rem', fontWeight: 500, lineHeight: '1.8125rem', mb: 1 }}>
            {interpolate(t.features.portabilityTitle, { brand })}
          </Typography>
          <Typography sx={{ color: '#A0A0A0', fontFamily: "'Figtree', sans-serif", fontSize: '0.875rem', fontWeight: 400, mb: 4 }}>
            {t.features.portabilityDesc}
          </Typography>

          <ComprarButton onClick={handleBuy} label={t.common.buy} />
        </Paper>

        {/* About Us Card */}
        <Paper
          id="sobre-nos"
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            borderRadius: '24px',
            bgcolor: 'var(--color-primary-dark, #4A0099)',
            color: '#fff',
            minWidth: { xs: '280px', md: 0 },
            flex: { xs: '0 0 auto', md: '1 1 22%' },
            scrollSnapAlign: 'start',
            scrollMarginTop: '100px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{
              ...labelTagDarkSx,
              mb: 4,
            }}
          >
            {t.features.aboutTag}
          </Box>
          <Typography sx={{ fontFamily: "'Figtree', sans-serif", fontSize: '2rem', fontWeight: 500, lineHeight: 1.1 }}>
            {t.features.aboutTitleLine1}
            <br />
            {t.features.aboutTitleLine2}
            <br />
            {t.features.aboutTitleLine3}
            <br />
            {t.features.aboutTitleLine4}
          </Typography>
        </Paper>

        {/* Image Card */}
        <Paper
          elevation={0}
          sx={{
            borderRadius: '24px',
            overflow: 'hidden',
            minHeight: 300,
            bgcolor: '#f5f5f5',
            backgroundImage: `url(${aboutImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            minWidth: { xs: '280px', md: 0 },
            flex: { xs: '0 0 auto', md: '1 1 22%' },
            scrollSnapAlign: 'start',
          }}
        />
      </Box>
    </Box>
  );
}
